/**
 * /s/:token — first-class shares model entry point.
 *
 * Responsibilities in Task 11 scope:
 *   1. Probe /api/share/:token; on 404 fall back to /api/watch/:token
 *      (legacy per-video shareToken from before migration 0013).
 *   2. If singleAsset video → auto-drill to /s/:token/v/:videoId.
 *   3. If legacy token → redirect to the existing /watch/:token page.
 *   4. If neither exists → 404.
 *   5. If it's a new-model multi-item share → render a minimal placeholder
 *      landing until Task 13 builds the real grid.
 *
 * Frontend router entry: registered at `/s/:token` in src/app/router.tsx.
 */

import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useShareEntry } from "@/hooks/use-share";
import { AuthLoading } from "@/components/auth/auth-loading";
import NotFoundPage from "@/pages/not-found";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/brand-mark";
import { ThemeToggle } from "@/components/theme-toggle";
import { ShareLandingPage } from "@/pages/share-landing";

export default function ShareEntryPage() {
  const { token } = useParams<{ token: string }>();
  // Task 3: `?preview=1` = team member opening via "Open Share Link" — skips
  // the guest identify form. Propagated to every downstream share URL so it
  // survives navigation into assets/folders.
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const pv = preview ? "?preview=1" : "";
  const { data, isLoading, error } = useShareEntry(token, preview);

  if (isLoading) return <AuthLoading />;

  // React Query threw — network error. Not the same as a 404 that came back
  // as { kind: 'not-found' } from the hook.
  if (error) {
    return (
      <ShareErrorShell
        title="Something went wrong"
        body="We couldn't load this share link. Check your connection and try again."
      />
    );
  }

  if (!data) return <NotFoundPage />;

  switch (data.kind) {
    case "not-found":
      return <NotFoundPage />;

    case "error":
      return (
        <ShareErrorShell
          title={
            data.error.status === 410
              ? "This link has expired"
              : data.error.status === 403
                ? "This share is unavailable"
                : "Something went wrong"
          }
          body={
            data.error.status === 410
              ? "Ask the owner for a fresh link."
              : data.error.status === 403
                ? "The owner has disabled this share or the workspace is suspended."
                : "We couldn't load this share link. Try again in a moment."
          }
        />
      );

    case "legacy":
      // Token belongs to the pre-0013 per-video model. The existing
      // /watch/:token page already handles that flow — keep old links live.
      return <Navigate to={`/watch/${encodeURIComponent(data.token)}`} replace />;

    case "share": {
      // singleAsset === true means: one item, and it's a video. Frame.io parity:
      // land the guest directly on the watch view. Multi-item / folder-included
      // shares stay on the grid landing.
      if (data.data.singleAsset) {
        const firstVideo = data.data.items.find((it) => it.type === "video");
        if (firstVideo) {
          return (
            <Navigate
              to={`/s/${encodeURIComponent(token!)}/v/${encodeURIComponent(firstVideo.id)}${pv}`}
              replace
            />
          );
        }
      }
      return <ShareLandingPage token={token!} data={data.data} preview={preview} />;
    }

    default:
      return <NotFoundPage />;
  }
}

function ShareErrorShell({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between px-4 py-4 md:px-8 md:py-6">
        <BrandMark />
        <ThemeToggle />
      </header>
      <main className="mx-auto flex max-w-lg flex-col items-center px-6 pt-32 text-center">
        <h1 className="font-display text-4xl font-black tracking-tight">{title}</h1>
        <p className="mt-3 text-muted-foreground">{body}</p>
        <Button asChild className="mt-8" variant="outline">
          <a href="/">Back home</a>
        </Button>
      </main>
    </div>
  );
}
